/**
 * LanguageService
 *
 * Manages the active language for the application. Wraps Transloco's
 * TranslocoService to add persistence (localStorage) and expose the
 * full list of South African official languages.
 *
 * Usage:
 *   inject(LanguageService).setLanguage('zu');
 *   inject(LanguageService).currentLanguage();   // signal
 */

import { computed, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { catchError, EMPTY, finalize } from 'rxjs';

export interface Language {
  /** BCP-47 / ISO 639-1 or -2 code used as the translation file name */
  code: string;
  /** English name */
  name: string;
  /** Native name (displayed in the language switcher) */
  nativeName: string;
}

/** All 11 South African official languages */
export const SA_LANGUAGES: Language[] = [
  { code: 'en',  name: 'English',        nativeName: 'English'    },
  { code: 'af',  name: 'Afrikaans',      nativeName: 'Afrikaans'  },
  { code: 'zu',  name: 'Zulu',           nativeName: 'isiZulu'    },
  { code: 'xh',  name: 'Xhosa',          nativeName: 'isiXhosa'   },
  { code: 'st',  name: 'Sotho',          nativeName: 'Sesotho'    },
  { code: 'tn',  name: 'Tswana',         nativeName: 'Setswana'   },
  { code: 'nso', name: 'Sepedi',         nativeName: 'Sepedi'     },
  { code: 'ts',  name: 'Tsonga',         nativeName: 'Xitsonga'   },
  { code: 'ss',  name: 'Swati',          nativeName: 'siSwati'    },
  { code: 've',  name: 'Venda',          nativeName: 'Tshivenḓa'  },
  { code: 'nr',  name: 'Ndebele',        nativeName: 'isiNdebele' },
];

const STORAGE_KEY = 'preferred_language';
const DEFAULT_LANG = 'en';

@Injectable({ providedIn: 'root' })
export class LanguageService {
  readonly languages = SA_LANGUAGES;

  /** Signal tracking the active language code */
  private readonly _activeLang = signal<string>(DEFAULT_LANG);
  readonly currentLanguage = this._activeLang.asReadonly();

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  /** Derived signal: the full Language object for the active code */
  readonly activeLanguage = computed(
    () => this.languages.find(l => l.code === this._activeLang()) ?? this.languages[0],
  );

  constructor(private readonly transloco: TranslocoService) {}

  /**
   * Reads the persisted preference from localStorage and activates it.
   * Call once inside provideAppInitializer or APP_INITIALIZER.
   */
  init(): void {
    const saved = localStorage.getItem(STORAGE_KEY) ?? DEFAULT_LANG;
    const isValid = this.languages.some(l => l.code === saved);
    this.applyLanguage(isValid ? saved : DEFAULT_LANG);
  }

  /** Switches the active language, persists it, and updates the HTML lang attribute. */
  setLanguage(code: string): void {
    const isValid = this.languages.some(l => l.code === code);
    if (!isValid) {
      console.warn(`[LanguageService] Unknown language code: "${code}". Falling back to "en".`);
      return;
    }
    this._isLoading.set(true);
    this.transloco.load(code).pipe(
      finalize(() => this._isLoading.set(false)),
      catchError(() => {
        console.warn(`[LanguageService] Failed to load language: "${code}".`);
        return EMPTY;
      }),
    ).subscribe(() => this.applyLanguage(code));
  }

  /** Returns the language code to send as the Accept-Language header to the API. */
  getAcceptLanguageHeader(): string {
    return this._activeLang();
  }

  /**
   * Returns a BCP-47 locale tag for the active language (e.g. 'zu-ZA', 'af-ZA').
   * All supported languages are South African, so the region is always ZA.
   * Use this wherever dates or numbers are formatted via Intl / toLocaleString.
   */
  getLocale(): string {
    return `${this._activeLang()}-ZA`;
  }

  private applyLanguage(code: string): void {
    this.transloco.setActiveLang(code);
    this._activeLang.set(code);
    localStorage.setItem(STORAGE_KEY, code);
    document.documentElement.setAttribute('lang', code);
  }
}
